import React, { useEffect, useState } from 'react'
import { View, StyleSheet, TouchableOpacity, KeyboardAvoidingView, ScrollView, SafeAreaView } from 'react-native'
import { Icon, Text, Button, CheckBox, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import MoneyTextInput from '../../components/MoneyTextInput'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { usePostHog } from 'posthog-react-native'
import { currencyFormatterAtom, discountOptionsListAtom, currentStoreSaleSettingsAtom, productModifiersListAtom, productUnitsDictAtom, productsDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { CHECKOUT_BLOCKED_REASONS, CHECKOUT_BLOCKED_SCREENS, CHECKOUT_BLOCKED_STAGES, captureCheckoutBlocked } from '../../utils/checkoutBlocked'
import { parseDecimalAmount } from '../../utils/decimalInput'


export default function EditCartItemScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const productsDict = useAtomValue(productsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const userRole = useAtomValue(userRoleAtom)
  const productModifiersList = useAtomValue(productModifiersListAtom)
  const fullDiscountOptionsList = useAtomValue(discountOptionsListAtom)
  const [discountOptionsList, setDiscountOptionsList] = useState(fullDiscountOptionsList)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const { index, cartItem, cartItems, setCartItems, store, addNewCartItem } = route.params
  const [quantity, setQuantity] = useState(cartItem.quantity.toString())
  const [price, setPrice] = useState(cartItem.salePrices?.[store]?.toString())
  const [name, setName] = useState(cartItem.name)
  const [purchasePrice, setPurchasePrice] = useState(cartItem.purchasePrice?.toString())
  const currentStoreSaleSettings = useAtomValue(currentStoreSaleSettingsAtom)
  // const [updatePriceChecked, setUpdatePriceChecked] = useState(false)
  const [itemDiscount, setItemDiscount] = useState(cartItem.itemDiscount)
  const [saveError, setSaveError] = useState('')
  const postHog = usePostHog()

  /**
   * ATL-1319 — this screen is on the checkout path, not beside it: a weighted or custom product
   * routes through here before its cart line is final (`needsEditor` in utils/cartAddition), so a
   * cashier who cannot get past it never reaches `submit()` at all. Every refusal below was
   * previously a red line of text and nothing else.
   *
   * Always the CART stage — nothing has been submitted yet — and always this screen, which is what
   * distinguishes these from the New Sale events.
   */
  const reportBlocked = (reason) => {
    captureCheckoutBlocked(postHog, reason, {
      screen: CHECKOUT_BLOCKED_SCREENS.EDIT_CART_ITEM,
      stage: CHECKOUT_BLOCKED_STAGES.CART,
    })
  }

  useEffect(() => {
    navigation.setOptions({
      title: cartItem?.name
    })
  }, [])
 
  useEffect(() => {
    let splicedDiscountOptions = [...fullDiscountOptionsList]
    splicedDiscountOptions.splice(1, 1)
    setDiscountOptionsList(splicedDiscountOptions)
  }, [fullDiscountOptionsList])

  const save = () => {
    // ATL-1319: the values are paired with the reason each one refuses for. The ORDER and the
    // early return are unchanged — this is still "first unreadable field wins, with the same
    // `notANumberError` message" — but all three fields previously produced one indistinguishable
    // message AND no event at all, so "the price box was empty" and "the quantity box was empty"
    // were the same observation from outside the device. They are different merchant mistakes.
    //
    // ATL-1371 — parsed ONCE, strictly, and reused below. `parseFloat` accepted the locale comma
    // a decimal keypad emits by truncating it ('36,5' → 36) and accepted '12abc' as 12, neither of
    // which is NaN, so neither reached the refusal below. It also read the two price boxes, which
    // rendered a DIGITS-ONLY keypad until this change, so a fractional price was unenterable.
    const numberQuantity = parseDecimalAmount(quantity)
    const numberPurchasePrice = parseDecimalAmount(purchasePrice)
    const numberPrice = parseDecimalAmount(price)
    const numberValuesToCheck = [
      [numberQuantity, CHECKOUT_BLOCKED_REASONS.INVALID_QUANTITY],
      [numberPurchasePrice, CHECKOUT_BLOCKED_REASONS.PRICE_MISSING],
      [numberPrice, CHECKOUT_BLOCKED_REASONS.PRICE_MISSING],
    ]
    for (let [parsedValue, reason] of numberValuesToCheck) {
      if (parsedValue === null) {
        setSaveError(t('notANumberError'))
        reportBlocked(reason)
        return
      }
    }
    // Beyond NaN: quantity must be positive and prices non-negative, so a 0/negative
    // entry can't produce a corrupt cart line.
    if (numberQuantity <= 0) {
      setSaveError(t('quantityMustBePositive') || 'Quantity must be greater than 0')
      reportBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_QUANTITY)
      return
    }
    if (numberPrice < 0 || numberPurchasePrice < 0) {
      setSaveError(t('priceMustBeNonNegative') || 'Price cannot be negative')
      // `invalid_price`, not `price_missing`: the number was entered and is readable, it is just
      // one the books cannot hold. A merchant fixing "I typed -1" is not a merchant fixing "I left
      // it blank", so they do not share a bucket.
      reportBlocked(CHECKOUT_BLOCKED_REASONS.INVALID_PRICE)
      return
    }
    var newSalePrices = {...cartItem.salePrices}
    newSalePrices[store] = numberPrice
    const updatedCartItem = {...cartItem, 
      quantity: numberQuantity, 
      salePrices: newSalePrices, 
      purchasePrice: numberPurchasePrice,
      name,
      itemDiscount: itemDiscount
    }
    var newCartItems = [...cartItems]
    newCartItems.splice(index, 1, updatedCartItem)
    setCartItems(newCartItems)
    navigation.navigate('NewSale')
  }

  const deleteItemFromCart = () => {
    var newCartItems = [...cartItems]
    if (newCartItems[index]?.isModified) {
      for (var i = index+1; i < newCartItems.length; i++) {
        if (newCartItems[i]?.isModifier) {
          newCartItems.splice(i, 1)
        } else {
          break
        }
      }
    }
    newCartItems.splice(index, 1)
    setCartItems(newCartItems)
    navigation.navigate('NewSale')
  }

  const addProductModifier = (productModifier) => {
    // var newCartItems = [...cartItems]
    // var newModifiers = cartItem.productModifiers ? [...cartItem.modifiers, productModifier] : [productModifier]
    // newCartItems.splice(index, 1, {...cartItem, productModifiers: newModifiers})
    // setCartItems(newCartItems)
    // navigation.navigate('NewSale')
    addNewCartItem({...productModifier, modifierQuantity: productModifier.quantity}, productModifier.quantity, true, index)
    navigation.goBack()
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
    <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
      { cartItem.id === 'customProduct' && (
        <>
          <Text category='label' style={styles.fieldLabel}>
            {t('newSale.name')}
          </Text>
          <StyledTextInput 
            style={styles.priceInput}
            autoCapitlize='sentences'
            value={name}
            onChangeText={text => setName(text)}
            selectTextOnFocus={true}
            returnKeyType='done'
          />
        </>
      )}
      <Text category='label' style={styles.fieldLabel}>
        {t('newSale.quantity')}
      </Text>
      <View style={styles.quantityContainer}>
        <TouchableOpacity onPress={() => {
          setQuantity((parseFloat(quantity) - 1).toString())
        }} style={styles.changeButton}>
          <Icon name='minus-circle-outline' fill={theme['text-hint-color']} height={30} width={30}  />
        </TouchableOpacity>
        <StyledTextInput 
          style={styles.quantityInput}
          keyboardType='decimal-pad'
          // autoFocus={true}
          value={quantity}
          controlledInput={true}
          onChangeText={text => setQuantity(text.replace(/,/g, '.'))}
          onSubmitEditing={save}
          placeholder="0.0"
          returnKeyType='done'
          selectTextOnFocus={true}
        />
        <TouchableOpacity testID="qty-plus-button" onPress={() => {
          setQuantity((parseFloat(quantity) + 1).toString())
        }} style={styles.changeButton}>
          <Icon name='plus-circle-outline' fill={theme['text-hint-color']} height={30} width={30} />
        </TouchableOpacity>
      </View>
      { (cartItem.id === 'customProduct' || currentStoreSaleSettings?.showPurchasePriceInEditCartItem) && (userRole.permissions?.['Products'] || userRole.id === 'admin') && (
        <>
          <Text category='label' style={styles.fieldLabel}>
            {t('newSale.purchasePrice')}
          </Text>
          <MoneyTextInput
            style={styles.priceInput}
            value={purchasePrice}
            onChangeText={text => setPurchasePrice(text)}
            selectTextOnFocus={true}
            returnKeyType='done'
          />
        </>
      )}
      { (userRole.permissions?.['Products'] || userRole.id === 'admin') && (
          <>
            <Text category='label' style={styles.fieldLabel}>
              {t('newSale.salePrice')}
            </Text>
            <MoneyTextInput
              style={styles.priceInput}
              value={price}
              onChangeText={text => setPrice(text)}
              placeholder="1000"
              selectTextOnFocus={true}
              returnKeyType='done'
            />
          </>
      )}
      { (userRole.permissions?.['applyDiscount'] || userRole.id === 'admin') && (
        <>
          <Text category='label' style={styles.fieldLabel}>
            {t('newSale.individualProductDiscount')}
          </Text>
          <PlatformDependantSelect
            data={discountOptionsList}
            selectedOption={itemDiscount}
            onSelect={(itemValue, itemIndex) => {
              setItemDiscount(itemValue)
            }}
            style={styles.discountSelect}
          />
        </>
      )}
      { productModifiersList?.map((productModifers, productModifiersIndex) => (
        <View style={styles.categoryContainer} key={productModifers.id}>
          {productModifers?.data?.length > 0 && <Text category='label' style={styles.categoryName}>
            {productModifers?.name}
          </Text>}
          <View style={styles.productsList}>
            { productModifers?.data?.map((product, productIndex) => (
              <TouchableOpacity key={product+productIndex} onPress={(e) => {
                  e.target.blur()
                  if (e.keyCode === 13) {
                    return
                  }
                  addProductModifier({...productsDict[product?.id], quantity: product?.quantity})
                }}
              >
                <View style={styles.productContainer}>  
                  <Text style={styles.productName}>
                    {productsDict[product.id]?.name}
                  </Text>
                  <Text style={styles.productPrice}>
                    {currencyFormatter.format(productsDict[product.id]?.salePrices?.[store] * product.quantity)}
                  </Text>
                  <Text style={styles.productPrice}>
                    {'(' + (productsDict[product.id]?.storeStock?.[store] ? productsDict[product.id]?.storeStock?.[store]?.toFixed(1) : 0) + ' ' + (productUnitsDict[product.productUnit]?.shortName || t('pcs')) + ')'}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ))}
      {saveError != '' && (
        <Text category='p1' status='danger' style={styles.fieldLabel}>
          {saveError}
        </Text>
      )}
      <View testID="edit-cart-delete-button">
        <Button style={styles.submitButton} appearance='outline' status='danger' onPress={deleteItemFromCart}>
          {t('newSale.deleteFromCart')}
        </Button>
      </View>
      <View testID="edit-cart-done-button">
        <Button style={styles.submitButton} appearance='outline' status='primary' onPress={save}>
          {t('done')}
        </Button>
      </View>
      <View style={{height: 50}}></View>
    </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
    // paddingTop: 5
    // alignItems: 'center',
  }, 
  fieldLabel: {
    marginTop: 10,
    marginBottom: 5,
  },
  quantityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    // marginVertical: 1,
  },
  changeButton: {
    // width: '5%',
    marginHorizontal: 3,
  },
  quantityInput: {
    flex: 1,
  },
  priceInput: {
    // marginBottom: 8,
  },
  discountSelect: {
    // marginVertical: 15,
  },
  submitButton: {
    marginTop: 10,
  },
  categoryName: {
    marginTop: 10
  },
  productsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  productContainer: {
    margin: 5,
    padding: 5,
    height: 80,
    width: 80,
    borderRadius: 5,
  },
  productName: {
    fontSize: 12,
    lineHeight: 12,
    marginBottom: 5,
    fontWeight: 'bold',
  },
  productPrice: {
    fontSize: 12,
    lineHeight: 14,
  },
});
